import { Children, cloneElement, isValidElement, useCallback, useEffect, useId, useMemo, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { RichTextView } from "./rich-text-editor";
import type { TiptapDoc } from "./rich-text-doc";
import { ErrorBanner, Skeleton, useConfirm, useToast } from "./ui-kit";
import { toVideoEmbed } from "./video-embed";

// Portal-admin authoring for onboarding plans -- delivery slice 6. Sits on top
// of the same /api/onboarding/* endpoints the CLI/tests use; published
// versions are read-only and "Clone to edit" forks a fresh draft.
//
// Everything user-facing here is deliberately phrased in the plan author's
// language rather than the schema's: an admin who did not write the migration
// should not have to know what `after_stage`, `work_item` or `retry_quiz`
// mean. The raw enum values still travel over the wire unchanged.

type Member = { displayName: string; id: string };

const api = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { "content-type": "application/json", ...init.headers } : init?.headers,
  });
  const payload = (await response.json()) as T & { message?: string };
  if (!response.ok) throw new Error(payload.message || "Taskei could not complete that request.");
  return payload;
};

const EVIDENCE_KINDS = [
  { hint: "The learner writes their evidence directly in Taskei.", label: "Written answer", value: "comment" },
  { hint: "The learner uploads a file on the task, then confirms.", label: "Uploaded file", value: "attachment" },
  { hint: "The learner supplies an external URL.", label: "Link", value: "link" },
  { hint: "The learner links another task or story as proof.", label: "Linked work item", value: "work_item" },
  { hint: "A timestamped confirmation, with no artifact. For briefs where reading and accepting is the point.", label: "Acknowledgement", value: "acknowledgement" },
  { hint: "Any one of the above satisfies the task.", label: "Any evidence", value: "any" },
] as const;

const TRIGGERS = [
  { hint: "An admin or mentor releases it by hand.", label: "Released manually", value: "manual" },
  { hint: "Fires once every task in the chosen stage is approved.", label: "After a stage is finished", value: "after_stage" },
  { hint: "Fires once the chosen tasks are all approved.", label: "After specific tasks", value: "after_tasks" },
  { hint: "Fires a set number of days after the learner starts.", label: "After a number of days", value: "after_days" },
] as const;

const ON_FAIL = [
  { hint: "Nothing resets. The learner simply retakes it.", label: "Just retake the quiz", value: "retry_quiz" },
  { hint: "Every task in the quiz's stage reopens and must be redone.", label: "Reopen the whole stage", value: "reset_stage" },
  { hint: "Every task on the entire plan reopens. Use with care.", label: "Reopen the entire plan", value: "reset_plan" },
] as const;

const QUESTION_KINDS = [
  { label: "Single choice", value: "single_choice" },
  { label: "Multiple choice", value: "multi_choice" },
  { label: "Written answer (mentor grades)", value: "short_text" },
] as const;

const labelFor = (options: ReadonlyArray<{ label: string; value: string }>, value: string) =>
  options.find((option) => option.value === value)?.label ?? value;

// The API rejects anything that fails /^[a-z0-9][a-z0-9-]{1,60}$/ with
// INVALID_KEY. Deriving the key from the title means an author never meets
// that error, while still being able to override it.
const slugify = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 61);
const KEY_PATTERN = /^[a-z0-9][a-z0-9-]{1,60}$/;

type PlanListItem = { id: string; key: string; title: string; publishedVersion: number | null; latestVersion: number | null };

type Task = {
  id: string; key: string; title: string; evidenceKind: string; evidenceMinChars: number | null;
  evidencePrompt: string | null; requiresApproval: boolean; dueOffsetDays: number | null;
  recurrence: "none" | "weekly"; prerequisiteKeys: string[];
  videoUrl: string | null; videoTitle: string | null;
  body: TiptapDoc | null;
};
type Stage = {
  id: string; key: string; title: string; kind: "sequential" | "parallel"; position: number; tasks: Task[];
  description: TiptapDoc | null;
};

// Same ordering the learner view uses (src/onboarding-page.tsx): every stage
// -- "Track" or "Phase" alike -- sits in one single ordered sequence by
// `position` (2026-09 onboarding UX change; this used to list every Phase
// before any Track regardless of position, back when a Track was reachable
// from the start and its position didn't affect gating). Reusing this here
// means an author previewing sees stages in the exact order a learner
// actually reads and unlocks them, instead of two components silently
// disagreeing on what "the order" even means.
const learnerStageOrder = (stages: Stage[]) => [...stages].sort((a, b) => a.position - b.position);
type Quiz = {
  id: string; key: string; title: string; trigger: string; passThresholdPct: number;
  maxAttempts: number | null; cooldownHours: number; onFail: string; questionCount: number;
  questions: Array<{ id: string; kind: string; points: number }>;
};
type PlanDetail = {
  plan: { id: string; key: string; title: string; teamId: string };
  versions: Array<{ id: string; version: number; status: string }>;
  selectedVersion: { id: string; version: number; status: string; stages: Stage[] };
  quizzes: Quiz[];
};

// A `<label>` wrapping a hint span puts the hint text INSIDE the control's
// accessible name (per the accname spec, every text descendant of a label
// counts) -- so `getByLabel("Mentor")` and every screen reader alike would
// announce "Mentor Reviews and approves the evidence." instead of "Mentor".
// Keeping the hint out of the label and wiring it in via aria-describedby
// still associates it with the control, just not as part of its name.
function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  const autoId = useId();
  const hintId = hint ? `${autoId}-hint` : undefined;
  // Most callers pass a single control; a few (e.g. KeyAndTitle's inline
  // validation message) pass the control plus trailing content. Only the
  // first element gets the id/aria-describedby wiring -- the rest renders
  // after it exactly as before.
  const [firstChild, ...rest] = Children.toArray(children);
  const control = isValidElement(firstChild)
    ? cloneElement(firstChild as ReactElement<{ "aria-describedby"?: string; id?: string }>, {
      id: (firstChild.props as { id?: string }).id ?? autoId,
      ...(hintId ? { "aria-describedby": [(firstChild.props as { "aria-describedby"?: string })["aria-describedby"], hintId].filter(Boolean).join(" ") } : {}),
    })
    : firstChild;
  const controlId = isValidElement(control) ? (control.props as { id?: string }).id : undefined;
  return (
    <div className="d-block small mb-2">
      <label className="fw-semibold d-block" htmlFor={controlId}>{label}</label>
      {control}
      {rest}
      {hint && <span className="text-body-secondary d-block mt-1" id={hintId}>{hint}</span>}
    </div>
  );
}

// A title+key pair where the key follows the title until an author edits it
// directly, then stops. Removes the single most common authoring error.
function KeyAndTitle({ keyValue, title, onChange, titleLabel }: {
  keyValue: string; title: string; titleLabel: string;
  onChange: (next: { key: string; title: string }) => void;
}) {
  const [keyTouched, setKeyTouched] = useState(false);
  const invalid = keyValue !== "" && !KEY_PATTERN.test(keyValue);
  return (
    <>
      <div className="flex-grow-1">
        <Field label={titleLabel}>
          <input
            className="form-control form-control-sm"
            value={title}
            onChange={(event) => onChange({
              key: keyTouched ? keyValue : slugify(event.target.value),
              title: event.target.value,
            })}
          />
        </Field>
      </div>
      <div>
        <Field label="Identifier" hint={invalid ? undefined : "Used in links and never shown to learners."}>
          <input
            className={`form-control form-control-sm ${invalid ? "is-invalid" : ""}`}
            value={keyValue}
            onChange={(event) => { setKeyTouched(true); onChange({ key: event.target.value, title }); }}
          />
          {invalid && <span className="invalid-feedback d-block">Lower-case letters, numbers and hyphens only.</span>}
        </Field>
      </div>
    </>
  );
}

function TaskEditor({ task, allTasks, editable, onChanged }: {
  task: Task; allTasks: Task[]; editable: boolean; onChanged: () => void;
}) {
  const [draft, setDraft] = useState(task);
  const [open, setOpen] = useState(false);
  const showToast = useToast();
  const confirm = useConfirm();

  const save = async () => {
    try {
      await api(`/api/onboarding/tasks/${task.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          title: draft.title, evidenceKind: draft.evidenceKind, requiresApproval: draft.requiresApproval,
          evidenceMinChars: draft.evidenceMinChars, dueOffsetDays: draft.dueOffsetDays, recurrence: draft.recurrence,
          evidencePrompt: draft.evidencePrompt, prerequisiteKeys: draft.prerequisiteKeys,
          videoUrl: draft.videoUrl ?? "", videoTitle: draft.videoTitle ?? "",
        }),
      });
      showToast("Task saved.", "success");
      setOpen(false);
      onChanged();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not save.", "error");
    }
  };

  const remove = async () => {
    if (!(await confirm({ title: "Delete task?", description: `“${task.title}” will be removed from this draft.`, tone: "danger" }))) return;
    await api(`/api/onboarding/tasks/${task.id}`, { method: "DELETE" });
    onChanged();
  };

  const togglePrerequisite = (key: string) => setDraft((current) => ({
    ...current,
    prerequisiteKeys: current.prerequisiteKeys.includes(key)
      ? current.prerequisiteKeys.filter((entry) => entry !== key)
      : [...current.prerequisiteKeys, key],
  }));
  const titleByKey = new Map(allTasks.map((entry) => [entry.key, entry.title]));

  return (
    <li className="list-group-item">
      <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <span>
          <span className="fw-semibold">{task.title}</span>
          <span className="text-body-secondary small ms-2">
            {labelFor(EVIDENCE_KINDS, task.evidenceKind)}
            {task.requiresApproval ? " · mentor approves" : " · self-serve"}
            {task.recurrence === "weekly" ? " · repeats weekly" : ""}
            {task.dueOffsetDays != null ? ` · due day ${task.dueOffsetDays}` : ""}
            {task.videoUrl ? " · has a video" : ""}
          </span>
          {task.prerequisiteKeys.length > 0 && (
            <span className="d-block text-body-secondary small">
              Unlocks after: {task.prerequisiteKeys.map((key) => titleByKey.get(key) ?? key).join("; ")}
            </span>
          )}
        </span>
        {editable && (
          <span className="d-flex gap-2">
            <button className="btn btn-sm btn-link p-0" onClick={() => setOpen((value) => !value)}>{open ? "Close" : "Edit"}</button>
            <button className="btn btn-sm btn-link text-danger p-0" onClick={() => void remove()}>Delete</button>
          </span>
        )}
      </div>
      {open && (
        <div className="mt-2 border-top pt-2">
          <Field label="Title"><input className="form-control form-control-sm" value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} /></Field>
          <Field label="What the learner is asked to submit" hint="Shown above the evidence box. This is the bar the mentor reviews against.">
            <textarea className="form-control form-control-sm" rows={3} value={draft.evidencePrompt ?? ""} onChange={(event) => setDraft({ ...draft, evidencePrompt: event.target.value })} />
          </Field>
          <div className="row g-2">
            <div className="col-md-4"><Field label="Evidence type" hint={EVIDENCE_KINDS.find((kind) => kind.value === draft.evidenceKind)?.hint}>
              <select className="form-select form-select-sm" value={draft.evidenceKind} onChange={(event) => setDraft({ ...draft, evidenceKind: event.target.value })}>
                {EVIDENCE_KINDS.map((kind) => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
              </select>
            </Field></div>
            <div className="col-md-4"><Field label="Minimum characters" hint="Written answers only. Blank means no minimum.">
              <input type="number" min={0} className="form-control form-control-sm" value={draft.evidenceMinChars ?? ""} onChange={(event) => setDraft({ ...draft, evidenceMinChars: event.target.value ? Number(event.target.value) : null })} />
            </Field></div>
            <div className="col-md-4"><Field label="Due (days after start)" hint="Blank means no due date.">
              <input type="number" min={0} className="form-control form-control-sm" value={draft.dueOffsetDays ?? ""} onChange={(event) => setDraft({ ...draft, dueOffsetDays: event.target.value ? Number(event.target.value) : null })} />
            </Field></div>
          </div>
          <div className="form-check"><input className="form-check-input" type="checkbox" id={`ra-${task.id}`} checked={draft.requiresApproval} onChange={(event) => setDraft({ ...draft, requiresApproval: event.target.checked })} /><label className="form-check-label small" htmlFor={`ra-${task.id}`}>A mentor must approve this before it counts</label></div>
          <div className="form-check mb-2"><input className="form-check-input" type="checkbox" id={`rec-${task.id}`} checked={draft.recurrence === "weekly"} onChange={(event) => setDraft({ ...draft, recurrence: event.target.checked ? "weekly" : "none" })} /><label className="form-check-label small" htmlFor={`rec-${task.id}`}>Repeats every week for the life of the plan</label></div>

          <div className="row g-2">
            <div className="col-md-8"><Field label="Video to watch" hint="A YouTube or Vimeo link. It plays inside Taskei, above the guidance. Leave blank for none.">
              <input
                className="form-control form-control-sm" placeholder="https://www.youtube.com/watch?v=..."
                value={draft.videoUrl ?? ""}
                onChange={(event) => setDraft({ ...draft, videoUrl: event.target.value || null })}
              />
            </Field></div>
            <div className="col-md-4"><Field label="Video label" hint="Shown under the player, e.g. the talk and speaker.">
              <input
                className="form-control form-control-sm"
                value={draft.videoTitle ?? ""}
                onChange={(event) => setDraft({ ...draft, videoTitle: event.target.value || null })}
              />
            </Field></div>
          </div>
          {draft.videoUrl && !toVideoEmbed(draft.videoUrl) && (
            <p className="small text-danger mb-2">
              That is not a YouTube or Vimeo video link, so no player would appear. Saving will be refused.
            </p>
          )}

          <fieldset className="mb-2">
            <legend className="small fw-semibold">Stays locked until these are approved</legend>
            {allTasks.filter((entry) => entry.key !== task.key).length === 0
              ? <p className="small text-body-secondary mb-0">No other tasks exist in this version yet.</p>
              : (
                <div className="border rounded p-2 overflow-auto" style={{ maxHeight: "12rem" }}>
                  {allTasks.filter((entry) => entry.key !== task.key).map((entry) => (
                    <div className="form-check" key={entry.key}>
                      <input
                        className="form-check-input" type="checkbox" id={`pre-${task.id}-${entry.key}`}
                        checked={draft.prerequisiteKeys.includes(entry.key)}
                        onChange={() => togglePrerequisite(entry.key)}
                      />
                      <label className="form-check-label small" htmlFor={`pre-${task.id}-${entry.key}`}>{entry.title}</label>
                    </div>
                  ))}
                </div>
              )}
          </fieldset>
          <button className="btn btn-sm btn-primary" onClick={() => void save()}>Save task</button>
        </div>
      )}
    </li>
  );
}

function AddTask({ versionId, stageKey, onChanged }: { versionId: string; stageKey: string; onChanged: () => void }) {
  const empty = {
    dueOffsetDays: "", evidenceKind: "comment", evidenceMinChars: "", evidencePrompt: "",
    key: "", requiresApproval: true, title: "",
  };
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const showToast = useToast();

  const add = async () => {
    try {
      // The create endpoint accepts every one of these, so there is no reason
      // to make the author add a task and then immediately reopen it to set
      // the fields that actually define the task.
      await api(`/api/onboarding/plan-versions/${versionId}/tasks`, {
        method: "POST",
        body: JSON.stringify({
          dueOffsetDays: form.dueOffsetDays ? Number(form.dueOffsetDays) : null,
          evidenceKind: form.evidenceKind,
          evidenceMinChars: form.evidenceMinChars ? Number(form.evidenceMinChars) : null,
          evidencePrompt: form.evidencePrompt || null,
          key: form.key, requiresApproval: form.requiresApproval, stageKey, title: form.title,
        }),
      });
      setForm(empty);
      setOpen(false);
      onChanged();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not add task.", "error");
    }
  };

  if (!open) {
    return (
      <li className="list-group-item bg-body-tertiary py-2">
        <button className="btn btn-sm btn-outline-primary" onClick={() => setOpen(true)}>+ Add a task</button>
      </li>
    );
  }

  return (
    <li className="list-group-item bg-body-tertiary">
      <div className="d-flex gap-2 flex-wrap align-items-start">
        <KeyAndTitle
          titleLabel="Task title" keyValue={form.key} title={form.title}
          onChange={(next) => setForm({ ...form, ...next })}
        />
      </div>
      <Field label="What the learner is asked to submit">
        <textarea className="form-control form-control-sm" rows={2} value={form.evidencePrompt} onChange={(event) => setForm({ ...form, evidencePrompt: event.target.value })} />
      </Field>
      <div className="row g-2">
        <div className="col-md-4"><Field label="Evidence type">
          <select className="form-select form-select-sm" value={form.evidenceKind} onChange={(event) => setForm({ ...form, evidenceKind: event.target.value })}>
            {EVIDENCE_KINDS.map((kind) => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
          </select>
        </Field></div>
        <div className="col-md-4"><Field label="Minimum characters"><input type="number" min={0} className="form-control form-control-sm" value={form.evidenceMinChars} onChange={(event) => setForm({ ...form, evidenceMinChars: event.target.value })} /></Field></div>
        <div className="col-md-4"><Field label="Due (days after start)"><input type="number" min={0} className="form-control form-control-sm" value={form.dueOffsetDays} onChange={(event) => setForm({ ...form, dueOffsetDays: event.target.value })} /></Field></div>
      </div>
      <div className="form-check mb-2">
        <input className="form-check-input" type="checkbox" id={`new-ra-${stageKey}`} checked={form.requiresApproval} onChange={(event) => setForm({ ...form, requiresApproval: event.target.checked })} />
        <label className="form-check-label small" htmlFor={`new-ra-${stageKey}`}>A mentor must approve this before it counts</label>
      </div>
      <div className="d-flex gap-2">
        <button className="btn btn-sm btn-primary" disabled={!KEY_PATTERN.test(form.key) || !form.title.trim()} onClick={() => void add()}>Add task</button>
        <button className="btn btn-sm btn-link" onClick={() => { setForm(empty); setOpen(false); }}>Cancel</button>
      </div>
    </li>
  );
}

type OptionDraft = { body: string; isCorrect: boolean };

// Replaces the old pipe-delimited "options (a|b|c)" + "correct (a|b)" pair,
// where correctness was matched by comparing option text and a single typo
// produced a question with no correct answer.
function OptionsEditor({ kind, options, onChange }: {
  kind: string; options: OptionDraft[]; onChange: (next: OptionDraft[]) => void;
}) {
  const setCorrect = (index: number, checked: boolean) => onChange(options.map((option, position) =>
    kind === "single_choice"
      ? { ...option, isCorrect: position === index }
      : position === index ? { ...option, isCorrect: checked } : option));

  return (
    <fieldset className="mb-2">
      <legend className="small fw-semibold">Answers · tick the correct {kind === "single_choice" ? "one" : "ones"}</legend>
      {options.map((option, index) => (
        <div className="d-flex align-items-center gap-2 mb-1" key={index}>
          <input
            className="form-check-input mt-0 flex-shrink-0"
            type={kind === "single_choice" ? "radio" : "checkbox"}
            name="correct-option"
            aria-label={`Answer ${index + 1} is correct`}
            checked={option.isCorrect}
            onChange={(event) => setCorrect(index, event.target.checked)}
          />
          <input
            className="form-control form-control-sm"
            aria-label={`Answer ${index + 1}`}
            placeholder={`Answer ${index + 1}`}
            value={option.body}
            onChange={(event) => onChange(options.map((entry, position) => position === index ? { ...entry, body: event.target.value } : entry))}
          />
          <button
            className="btn btn-sm btn-link text-danger p-0 flex-shrink-0"
            aria-label={`Remove answer ${index + 1}`}
            disabled={options.length <= 2}
            onClick={() => onChange(options.filter((_, position) => position !== index))}
          >
            Remove
          </button>
        </div>
      ))}
      <button className="btn btn-sm btn-link p-0" onClick={() => onChange([...options, { body: "", isCorrect: false }])}>+ Add answer</button>
    </fieldset>
  );
}

function QuizEditor({ quiz, editable, onChanged }: {
  quiz: Quiz; editable: boolean; onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("single_choice");
  const [explanation, setExplanation] = useState("");
  const [options, setOptions] = useState<OptionDraft[]>([{ body: "", isCorrect: true }, { body: "", isCorrect: false }]);
  const showToast = useToast();
  const confirm = useConfirm();

  const filled = options.filter((option) => option.body.trim());
  const correctCount = filled.filter((option) => option.isCorrect).length;
  // Mirror the server's own rules (TOO_FEW_OPTIONS / NO_CORRECT_OPTION /
  // SINGLE_CHOICE_ONE_CORRECT) so the author sees the problem while typing.
  const problem = kind === "short_text" ? null
    : filled.length < 2 ? "Add at least two answers."
    : correctCount === 0 ? "Tick the correct answer."
    : kind === "single_choice" && correctCount !== 1 ? "Single-choice questions need exactly one correct answer."
    : null;

  const addQuestion = async () => {
    try {
      await api(`/api/onboarding/quizzes/${quiz.id}/questions`, {
        method: "POST",
        body: JSON.stringify({
          body: { type: "doc", content: [{ type: "paragraph", content: body ? [{ type: "text", text: body }] : [] }] },
          explanation: explanation || null,
          kind,
          options: kind === "short_text" ? [] : filled,
        }),
      });
      setBody(""); setExplanation("");
      setOptions([{ body: "", isCorrect: true }, { body: "", isCorrect: false }]);
      onChanged();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not add question.", "error");
    }
  };

  const remove = async () => {
    if (!(await confirm({ title: "Delete quiz?", description: `“${quiz.title}” and its questions will be removed.`, tone: "danger" }))) return;
    await api(`/api/onboarding/quizzes/${quiz.id}`, { method: "DELETE" });
    onChanged();
  };

  return (
    <li className="list-group-item">
      <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <span>
          <span className="fw-semibold">{quiz.title}</span>
          <span className="text-body-secondary small ms-2">
            {labelFor(TRIGGERS, quiz.trigger)} · pass ≥ {quiz.passThresholdPct}% · {quiz.questionCount} question{quiz.questionCount === 1 ? "" : "s"}
            {quiz.onFail !== "retry_quiz" ? ` · on failure: ${labelFor(ON_FAIL, quiz.onFail).toLowerCase()}` : ""}
          </span>
          {quiz.questionCount === 0 && (
            <span className="d-block small text-danger">No questions yet — a learner cannot take this quiz.</span>
          )}
        </span>
        {editable && (
          <span className="d-flex gap-2">
            <button className="btn btn-sm btn-link p-0" onClick={() => setOpen((value) => !value)}>{open ? "Close" : "Questions"}</button>
            <button className="btn btn-sm btn-link text-danger p-0" onClick={() => void remove()}>Delete</button>
          </span>
        )}
      </div>
      {open && (
        <div className="mt-2 border-top pt-2">
          <ol className="small">
            {quiz.questions.map((question) => (
              <li key={question.id}>{labelFor(QUESTION_KINDS, question.kind)} · {question.points} point{question.points === 1 ? "" : "s"}</li>
            ))}
          </ol>
          <Field label="Question"><textarea className="form-control form-control-sm" rows={2} value={body} onChange={(event) => setBody(event.target.value)} /></Field>
          <Field label="Question type">
            <select className="form-select form-select-sm w-auto" value={kind} onChange={(event) => setKind(event.target.value)}>
              {QUESTION_KINDS.map((entry) => <option key={entry.value} value={entry.value}>{entry.label}</option>)}
            </select>
          </Field>
          {kind !== "short_text" && <OptionsEditor kind={kind} options={options} onChange={setOptions} />}
          <Field label="Explanation" hint="Shown to the learner after grading. Optional.">
            <input className="form-control form-control-sm" value={explanation} onChange={(event) => setExplanation(event.target.value)} />
          </Field>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-sm btn-outline-primary" disabled={!body.trim() || Boolean(problem)} onClick={() => void addQuestion()}>Add question</button>
            {problem && <span className="small text-body-secondary">{problem}</span>}
          </div>
        </div>
      )}
    </li>
  );
}

function AddQuiz({ versionId, stages, tasks, onChanged }: {
  versionId: string; stages: Stage[]; tasks: Task[]; onChanged: () => void;
}) {
  const empty = {
    confirmResetPlan: false, cooldownHours: "24", key: "", maxAttempts: "3", onFail: "retry_quiz",
    passThresholdPct: 80, stageKey: "", title: "", trigger: "manual", triggerAfterDays: "",
    triggerTaskKeys: [] as string[],
  };
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const showToast = useToast();
  const set = (patch: Partial<typeof form>) => setForm((current) => ({ ...current, ...patch }));

  const add = async () => {
    try {
      await api(`/api/onboarding/plan-versions/${versionId}/quizzes`, {
        method: "POST",
        body: JSON.stringify({
          confirmResetPlan: form.confirmResetPlan,
          cooldownHours: Number(form.cooldownHours || 0),
          key: form.key,
          maxAttempts: form.maxAttempts ? Number(form.maxAttempts) : null,
          onFail: form.onFail,
          passThresholdPct: Number(form.passThresholdPct),
          stageKey: form.trigger === "after_stage" ? form.stageKey : undefined,
          title: form.title,
          trigger: form.trigger,
          triggerAfterDays: form.trigger === "after_days" ? Number(form.triggerAfterDays) : undefined,
          triggerTaskKeys: form.trigger === "after_tasks" ? form.triggerTaskKeys : undefined,
        }),
      });
      setForm(empty);
      setOpen(false);
      onChanged();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not add quiz.", "error");
    }
  };

  if (!open) {
    return (
      <li className="list-group-item bg-body-tertiary py-2">
        <button className="btn btn-sm btn-outline-primary" onClick={() => setOpen(true)}>+ Add a checkpoint quiz</button>
      </li>
    );
  }

  // The API rejects a resetting quiz with no attempt bound (UNBOUNDED_RETRY),
  // because that configuration permits an endless fail-reset-fail loop.
  const unbounded = form.onFail !== "retry_quiz" && !form.maxAttempts && Number(form.cooldownHours || 0) === 0;
  const missingTrigger =
    (form.trigger === "after_stage" && !form.stageKey)
    || (form.trigger === "after_days" && !form.triggerAfterDays)
    || (form.trigger === "after_tasks" && form.triggerTaskKeys.length === 0);

  return (
    <li className="list-group-item bg-body-tertiary">
      <div className="d-flex gap-2 flex-wrap align-items-start">
        <KeyAndTitle titleLabel="Quiz title" keyValue={form.key} title={form.title} onChange={(next) => set(next)} />
      </div>
      <div className="row g-2">
        <div className="col-md-6"><Field label="When does it appear?" hint={TRIGGERS.find((entry) => entry.value === form.trigger)?.hint}>
          <select className="form-select form-select-sm" value={form.trigger} onChange={(event) => set({ trigger: event.target.value })}>
            {TRIGGERS.map((entry) => <option key={entry.value} value={entry.value}>{entry.label}</option>)}
          </select>
        </Field></div>
        {form.trigger === "after_stage" && (
          <div className="col-md-6"><Field label="After which stage?" hint="It also blocks every stage positioned after this one until passed.">
            <select className="form-select form-select-sm" value={form.stageKey} onChange={(event) => set({ stageKey: event.target.value })}>
              <option value="">Choose a stage…</option>
              {stages.map((stage) => <option key={stage.key} value={stage.key}>{stage.title}</option>)}
            </select>
          </Field></div>
        )}
        {form.trigger === "after_days" && (
          <div className="col-md-6"><Field label="How many days after the learner starts?">
            <input type="number" min={1} className="form-control form-control-sm" value={form.triggerAfterDays} onChange={(event) => set({ triggerAfterDays: event.target.value })} />
          </Field></div>
        )}
      </div>
      {form.trigger === "after_tasks" && (
        <fieldset className="mb-2">
          <legend className="small fw-semibold">Appears once all of these are approved</legend>
          {tasks.length === 0 ? <p className="small text-body-secondary mb-0">Add some tasks first.</p> : (
            <div className="border rounded p-2 overflow-auto bg-body" style={{ maxHeight: "12rem" }}>
              {tasks.map((task) => (
                <div className="form-check" key={task.key}>
                  <input
                    className="form-check-input" type="checkbox" id={`trig-${task.key}`}
                    checked={form.triggerTaskKeys.includes(task.key)}
                    onChange={() => set({
                      triggerTaskKeys: form.triggerTaskKeys.includes(task.key)
                        ? form.triggerTaskKeys.filter((key) => key !== task.key)
                        : [...form.triggerTaskKeys, task.key],
                    })}
                  />
                  <label className="form-check-label small" htmlFor={`trig-${task.key}`}>{task.title}</label>
                </div>
              ))}
            </div>
          )}
        </fieldset>
      )}
      <div className="row g-2">
        <div className="col-md-3"><Field label="Pass mark (%)"><input type="number" min={1} max={100} className="form-control form-control-sm" value={form.passThresholdPct} onChange={(event) => set({ passThresholdPct: Number(event.target.value) })} /></Field></div>
        <div className="col-md-3"><Field label="Max attempts" hint="Blank means unlimited."><input type="number" min={1} className="form-control form-control-sm" value={form.maxAttempts} onChange={(event) => set({ maxAttempts: event.target.value })} /></Field></div>
        <div className="col-md-3"><Field label="Cooldown (hours)" hint="Wait after a failed attempt."><input type="number" min={0} className="form-control form-control-sm" value={form.cooldownHours} onChange={(event) => set({ cooldownHours: event.target.value })} /></Field></div>
        <div className="col-md-3"><Field label="If the learner fails" hint={ON_FAIL.find((entry) => entry.value === form.onFail)?.hint}>
          <select className="form-select form-select-sm" value={form.onFail} onChange={(event) => set({ onFail: event.target.value })}>
            {ON_FAIL.map((entry) => <option key={entry.value} value={entry.value}>{entry.label}</option>)}
          </select>
        </Field></div>
      </div>
      {form.onFail === "reset_plan" && (
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id="confirm-reset-plan" checked={form.confirmResetPlan} onChange={(event) => set({ confirmResetPlan: event.target.checked })} />
          <label className="form-check-label small text-danger" htmlFor="confirm-reset-plan">I understand that failing this reopens every completed task on the plan.</label>
        </div>
      )}
      {unbounded && (
        <p className="small text-danger mb-1">A quiz that reopens work must set max attempts or a cooldown, or a learner can loop forever.</p>
      )}
      <div className="d-flex gap-2 mt-2">
        <button
          className="btn btn-sm btn-primary"
          disabled={!KEY_PATTERN.test(form.key) || !form.title.trim() || missingTrigger || unbounded || (form.onFail === "reset_plan" && !form.confirmResetPlan)}
          onClick={() => void add()}
        >
          Add quiz
        </button>
        <button className="btn btn-sm btn-link" onClick={() => { setForm(empty); setOpen(false); }}>Cancel</button>
      </div>
    </li>
  );
}

function StageHeader({ stage, editable, onChanged }: { stage: Stage; editable: boolean; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ kind: stage.kind, title: stage.title });
  const showToast = useToast();
  const confirm = useConfirm();

  const save = async () => {
    try {
      await api(`/api/onboarding/stages/${stage.id}`, { method: "PATCH", body: JSON.stringify(draft) });
      showToast("Stage updated.", "success");
      setEditing(false);
      onChanged();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not update the stage.", "error");
    }
  };

  const remove = async () => {
    if (!(await confirm({
      title: "Delete stage?",
      description: `“${stage.title}” and its ${stage.tasks.length} task${stage.tasks.length === 1 ? "" : "s"} will be removed from this draft.`,
      confirmLabel: "Delete stage", tone: "danger",
    }))) return;
    try {
      await api(`/api/onboarding/stages/${stage.id}`, { method: "DELETE" });
      onChanged();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not delete the stage.", "error");
    }
  };

  if (editing) {
    return (
      <div className="card-header d-flex gap-2 flex-wrap align-items-end">
        <label className="small flex-grow-1">Stage title
          <input className="form-control form-control-sm" value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} />
        </label>
        <label className="small">Type
          <select className="form-select form-select-sm" value={draft.kind} onChange={(event) => setDraft({ ...draft, kind: event.target.value as Stage["kind"] })}>
            <option value="sequential">Phase — unlocks in order</option>
            <option value="parallel">Track — open from the start</option>
          </select>
        </label>
        <button className="btn btn-sm btn-primary" disabled={!draft.title.trim()} onClick={() => void save()}>Save</button>
        <button className="btn btn-sm btn-link" onClick={() => { setDraft({ kind: stage.kind, title: stage.title }); setEditing(false); }}>Cancel</button>
      </div>
    );
  }

  return (
    <div className="card-header d-flex justify-content-between align-items-center gap-2 flex-wrap">
      <span>
        <span className="fw-semibold">{stage.title}</span>
        <span className="badge text-bg-light ms-2">{stage.kind === "parallel" ? "Track" : "Phase"}</span>
        <span className="text-body-secondary small ms-2">{stage.tasks.length} task{stage.tasks.length === 1 ? "" : "s"}</span>
      </span>
      {editable && (
        <span className="d-flex gap-2">
          <button className="btn btn-sm btn-link p-0" onClick={() => setEditing(true)}>Edit</button>
          <button className="btn btn-sm btn-link text-danger p-0" onClick={() => void remove()}>Delete</button>
        </span>
      )}
    </div>
  );
}

// Read-only, in the same position order and with the same content
// (description/body/video/evidence prompt) a learner would see -- there was
// previously no way to check what a plan actually looks like without
// publishing it and assigning it to a real person. Task states (locked, open,
// approved) aren't simulated; this is about content and order, not progress.
function PlanPreview({ stages }: { stages: Stage[] }) {
  const ordered = learnerStageOrder(stages);
  if (!ordered.length) return <p className="text-body-secondary">This version has no stages yet.</p>;
  return (
    <div className="d-flex flex-column gap-3">
      {ordered.map((stage) => (
        <div className="card" key={stage.id}>
          <div className="card-header">
            <span className="fw-semibold">{stage.title}</span>
            <span className="badge text-bg-light ms-2">{stage.kind === "sequential" ? "Phase" : "Track"}</span>
          </div>
          <div className="card-body">
            {stage.description && <RichTextView doc={stage.description} />}
            {stage.tasks.length === 0
              ? <p className="small text-body-secondary mb-0">No tasks in this stage yet.</p>
              : (
                <ul className="list-group list-group-flush">
                  {stage.tasks.map((task) => {
                    const embed = task.videoUrl ? toVideoEmbed(task.videoUrl) : null;
                    return (
                      <li className="list-group-item" key={task.id}>
                        <span className="fw-semibold d-block">{task.title}</span>
                        {embed && (
                          <div className="ratio ratio-16x9 my-2" style={{ maxWidth: "30rem" }}>
                            <iframe src={embed.embedUrl} title={task.videoTitle ?? task.title} allowFullScreen />
                          </div>
                        )}
                        {task.body && <RichTextView doc={task.body} />}
                        {task.evidencePrompt && <p className="small text-body-secondary mb-0 mt-1">Asks for: {task.evidencePrompt}</p>}
                      </li>
                    );
                  })}
                </ul>
              )}
          </div>
        </div>
      ))}
    </div>
  );
}

function PlanEditor({ planId, onClose }: { planId: string; onClose: () => void }) {
  const [detail, setDetail] = useState<PlanDetail | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [newStage, setNewStage] = useState({ key: "", kind: "sequential", title: "" });
  const [addingStage, setAddingStage] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const showToast = useToast();
  const confirm = useConfirm();

  // `versionOverride` lets a caller bypass the `selectedVersion` state value
  // for this one call -- needed by deleteVersion below, where the version
  // just deleted is still what `selectedVersion` holds (state updates aren't
  // visible until the next render) and re-requesting it would 404.
  const load = useCallback(async (versionOverride?: number | null) => {
    const target = versionOverride === undefined ? selectedVersion : versionOverride;
    const query = target ? `?version=${target}` : "";
    const result = await api<PlanDetail>(`/api/onboarding/plans/${planId}${query}`);
    setDetail(result);
    setSelectedVersion(result.selectedVersion.version);
  }, [planId, selectedVersion]);

  useEffect(() => { void load().catch((reason) => showToast(reason instanceof Error ? reason.message : "Could not load plan.", "error")); }, [load, showToast]);

  if (!detail) return <Skeleton label="Loading plan…" variant="list" />;
  const version = detail.selectedVersion;
  const editable = version.status === "draft";
  const allTasks = version.stages.flatMap((stage) => stage.tasks);

  const addStage = async () => {
    try {
      await api(`/api/onboarding/plan-versions/${version.id}/stages`, { method: "POST", body: JSON.stringify(newStage) });
      setNewStage({ key: "", kind: "sequential", title: "" });
      setAddingStage(false);
      await load();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not add stage.", "error");
    }
  };
  const act = async (path: string, successMessage: string) => {
    // `load(null)` (not bare `load()`) on purpose: `load`'s no-arg path
    // reads `selectedVersion` from *this* closure, which is still the
    // version showing on screen right now, not the one this action just
    // produced. Calling `setSelectedVersion(null)` first and then `load()`
    // looks like it should fetch the latest version, but doesn't -- the
    // state update isn't visible in this closure, so it re-requests the old
    // version instead, and separately the `selectedVersion` change cascades
    // into the mount effect re-running `load()` a second time with the
    // correct target -- two requests racing, latest-write-wins on which one
    // the screen ends up showing. Passing the override sidesteps the race
    // by never taking the stale path at all.
    try { await api(path, { method: "POST" }); showToast(successMessage, "success"); await load(null); }
    catch (reason) { showToast(reason instanceof Error ? reason.message : "Failed.", "error"); }
  };

  // Publishing freezes the version permanently, so it gets a confirmation that
  // states what is being frozen rather than a one-click button.
  const publish = async () => {
    const emptyQuizzes = detail.quizzes.filter((quiz) => quiz.questionCount === 0);
    if (!(await confirm({
      title: `Publish version ${version.version}?`,
      description: `${version.stages.length} stage${version.stages.length === 1 ? "" : "s"} and ${allTasks.length} task${allTasks.length === 1 ? "" : "s"} will be frozen and become assignable. Published versions cannot be edited — you would clone to a new draft instead.${emptyQuizzes.length ? ` Warning: ${emptyQuizzes.length} quiz(zes) still have no questions and would block learners permanently.` : ""}`,
      confirmLabel: "Publish",
      tone: emptyQuizzes.length ? "danger" : "default",
    }))) return;
    await act(`/api/onboarding/plan-versions/${version.id}/publish`, "Published.");
  };

  // Works on a draft or a published version -- the server is the actual
  // gatekeeper (refuses the plan's last remaining version, and any version
  // with a learner assigned to it), so the confirmation here just states the
  // blast radius rather than re-deriving those rules client-side.
  const deleteVersion = async () => {
    if (!(await confirm({
      title: `Delete version ${version.version}?`,
      description: `This permanently removes ${version.stages.length} stage${version.stages.length === 1 ? "" : "s"}, ${allTasks.length} task${allTasks.length === 1 ? "" : "s"}, and any quizzes in this version. This cannot be undone.`,
      confirmLabel: "Delete version",
      tone: "danger",
    }))) return;
    try {
      await api(`/api/onboarding/plan-versions/${version.id}`, { method: "DELETE" });
      showToast(`Version ${version.version} deleted.`, "success");
      await load(null);
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not delete version.", "error");
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <h3 className="h5 mb-0">{detail.plan.title}</h3>
        <span className="d-flex gap-2">
          <button className={`btn btn-sm ${previewing ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => setPreviewing((value) => !value)}>
            {previewing ? "Back to editing" : "Preview as learner"}
          </button>
          <button className="btn btn-sm btn-link" onClick={onClose}>← All plans</button>
        </span>
      </div>

      {previewing ? (
        <PlanPreview stages={version.stages} />
      ) : (
      <>
      <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
        <label className="small">
          <span className="visually-hidden">Version</span>
          <select className="form-select form-select-sm w-auto" value={version.version} onChange={(event) => setSelectedVersion(Number(event.target.value))}>
            {detail.versions.map((entry) => (
              <option key={entry.id} value={entry.version}>
                Version {entry.version} · {entry.status === "draft" ? "draft (editable)" : entry.status}
              </option>
            ))}
          </select>
        </label>
        {editable
          ? (
            <>
              {/* The server refuses EMPTY_PLAN_VERSION, so do not offer a
                  publish that was always going to be rejected. */}
              <button className="btn btn-sm btn-success" disabled={allTasks.length === 0} onClick={() => void publish()}>
                Publish version {version.version}
              </button>
              {allTasks.length === 0 && <span className="small text-body-secondary">Add at least one task before publishing.</span>}
            </>
          )
          : <button className="btn btn-sm btn-outline-primary" onClick={() => void act(`/api/onboarding/plans/${planId}/versions`, "Cloned to a new draft.")}>Clone to edit</button>}
        {/* A plan always needs at least one version -- see deleteVersion's
            comment in onboarding-api.ts -- so hide the option entirely
            rather than let everyone hit the same 409. */}
        {detail.versions.length > 1 && (
          <button className="btn btn-sm btn-outline-danger" onClick={() => void deleteVersion()}>
            Delete version {version.version}
          </button>
        )}
      </div>

      {!editable && (
        <div className="alert alert-secondary py-2 small">
          Version {version.version} is {version.status} and cannot be changed — learners already assigned to it must
          keep seeing the same content. Use <span className="fw-semibold">Clone to edit</span> to start a new draft.
        </div>
      )}

      {version.stages.length === 0 && (
        <div className="alert alert-info py-2 small">
          This version has no stages yet. Add a <span className="fw-semibold">Phase</span> for work that unlocks in
          order, or a <span className="fw-semibold">Track</span> for topics the learner can pick up at any time.
        </div>
      )}

      {learnerStageOrder(version.stages).map((stage) => (
        <div className="card mb-3" key={stage.id}>
          <StageHeader stage={stage} editable={editable} onChanged={load} />
          <ul className="list-group list-group-flush">
            {stage.tasks.map((task) => (
              <TaskEditor key={task.id} task={task} allTasks={allTasks} editable={editable} onChanged={load} />
            ))}
            {editable && <AddTask versionId={version.id} stageKey={stage.key} onChanged={load} />}
          </ul>
        </div>
      ))}

      {editable && (addingStage ? (
        <div className="card mb-3 border-primary-subtle">
          <div className="card-body">
            <div className="d-flex gap-2 flex-wrap align-items-start">
              <KeyAndTitle titleLabel="Stage title" keyValue={newStage.key} title={newStage.title} onChange={(next) => setNewStage({ ...newStage, ...next })} />
              <div>
                <Field label="Type" hint="Every stage unlocks in position order now, whichever you pick here -- this only changes the label shown (“Phase” vs “Track”).">
                  <select className="form-select form-select-sm" value={newStage.kind} onChange={(event) => setNewStage({ ...newStage, kind: event.target.value })}>
                    <option value="sequential">Phase</option>
                    <option value="parallel">Track</option>
                  </select>
                </Field>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-primary" disabled={!KEY_PATTERN.test(newStage.key) || !newStage.title.trim()} onClick={() => void addStage()}>Add stage</button>
              <button className="btn btn-sm btn-link" onClick={() => setAddingStage(false)}>Cancel</button>
            </div>
          </div>
        </div>
      ) : (
        <button className="btn btn-sm btn-outline-primary mb-3" onClick={() => setAddingStage(true)}>+ Add a stage</button>
      ))}

      <div className="card">
        <div className="card-header fw-semibold">Checkpoint quizzes</div>
        <ul className="list-group list-group-flush">
          {detail.quizzes.length === 0 && !editable && (
            <li className="list-group-item small text-body-secondary">This version has no checkpoint quizzes.</li>
          )}
          {detail.quizzes.map((quiz) => <QuizEditor key={quiz.id} quiz={quiz} editable={editable} onChanged={load} />)}
          {editable && <AddQuiz versionId={version.id} stages={version.stages} tasks={allTasks} onChanged={load} />}
        </ul>
      </div>
      </>
      )}
    </div>
  );
}

function PlanRow({ plan, teams, onOpen, onChanged }: {
  plan: PlanListItem; teams: Array<{ id: string; name: string }>; onOpen: () => void; onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ key: plan.key, title: plan.title });
  const [busy, setBusy] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [targetTeamId, setTargetTeamId] = useState("");
  const showToast = useToast();
  const confirm = useConfirm();

  // The PM/ML intern plans were seeded as reusable templates, but until this
  // there was no way to actually reuse one -- every other team had to
  // re-author the whole stage/task/quiz tree by hand.
  const duplicate = async () => {
    setBusy(true);
    try {
      const result = await api<{ key: string }>(`/api/onboarding/plans/${plan.id}/duplicate`, {
        method: "POST",
        body: JSON.stringify({ targetTeamId }),
      });
      const teamName = teams.find((team) => team.id === targetTeamId)?.name ?? "the destination team";
      showToast(`Duplicated as “${result.key}” on ${teamName}.`, "success");
      setDuplicating(false);
      setTargetTeamId("");
      onChanged();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not duplicate the plan.", "error");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api(`/api/onboarding/plans/${plan.id}`, { method: "PATCH", body: JSON.stringify({ key: draft.key.trim(), title: draft.title.trim() }) });
      showToast("Plan updated.", "success");
      setEditing(false);
      onChanged();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not update the plan.", "error");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!(await confirm({
      title: "Delete this plan?",
      description: `“${plan.title}” and every version, quiz, and assignment will be permanently deleted, along with the onboarding tasks already created for learners. This cannot be undone.`,
      confirmLabel: "Delete plan",
      tone: "danger",
    }))) return;
    setBusy(true);
    try {
      const result = await api<{ removedWorkItems: number }>(`/api/onboarding/plans/${plan.id}`, { method: "DELETE" });
      showToast(`Plan deleted${result.removedWorkItems ? ` (${result.removedWorkItems} onboarding task${result.removedWorkItems === 1 ? "" : "s"} removed)` : ""}.`, "success");
      onChanged();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not delete the plan.", "error");
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <li className="list-group-item d-flex gap-2 flex-wrap align-items-end">
        <label className="small flex-grow-1">Plan title
          <input className="form-control form-control-sm" value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} />
        </label>
        <label className="small">Identifier
          <input className="form-control form-control-sm" value={draft.key} onChange={(event) => setDraft({ ...draft, key: event.target.value })} />
        </label>
        <button className="btn btn-sm btn-primary" disabled={busy || !KEY_PATTERN.test(draft.key.trim()) || !draft.title.trim()} onClick={() => void save()}>Save</button>
        <button className="btn btn-sm btn-link" disabled={busy} onClick={() => { setEditing(false); setDraft({ key: plan.key, title: plan.title }); }}>Cancel</button>
      </li>
    );
  }

  return (
    <li className="list-group-item">
      <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <span>
          <span className="fw-semibold">{plan.title}</span>
          <span className={`badge ms-2 ${plan.publishedVersion ? "text-bg-success" : "text-bg-secondary"}`}>
            {plan.publishedVersion ? `Published v${plan.publishedVersion}` : "Draft — not assignable yet"}
          </span>
        </span>
        <span className="d-flex gap-2 align-items-center">
          <button className="btn btn-sm btn-outline-primary" onClick={onOpen}>Open</button>
          <button className="btn btn-sm btn-link p-0" disabled={busy} onClick={() => setEditing(true)}>Rename</button>
          <button className="btn btn-sm btn-link p-0" disabled={busy} onClick={() => setDuplicating((value) => !value)}>Duplicate</button>
          <button className="btn btn-sm btn-link text-danger p-0" disabled={busy} onClick={() => void remove()}>Delete</button>
        </span>
      </div>
      {duplicating && (
        <div className="d-flex gap-2 align-items-center mt-2">
          <label className="small flex-grow-1">
            <span className="visually-hidden">Destination team for the duplicate</span>
            <select className="form-select form-select-sm" value={targetTeamId} onChange={(event) => setTargetTeamId(event.target.value)}>
              <option value="">Choose a team…</option>
              {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
            </select>
          </label>
          <button className="btn btn-sm btn-primary" disabled={busy || !targetTeamId} onClick={() => void duplicate()}>Duplicate here</button>
          <button className="btn btn-sm btn-link" disabled={busy} onClick={() => setDuplicating(false)}>Cancel</button>
        </div>
      )}
    </li>
  );
}

function AssignPanel({ teamId, members, plans, onAssigned }: {
  teamId: string; members: Member[]; plans: PlanListItem[]; onAssigned: () => void;
}) {
  const [form, setForm] = useState({ mentorUserId: "", planId: "", startedOn: new Date().toISOString().slice(0, 10), targetOn: "", userIds: [] as string[] });
  const [assigned, setAssigned] = useState<Array<{ planKey: string; userId: string }>>([]);
  const [busy, setBusy] = useState(false);
  // Per-person outcomes from the last submit -- assigning a cohort means some
  // members can succeed while one fails (already assigned, say), and silently
  // dropping that on the floor is worse than one shared toast.
  const [lastResults, setLastResults] = useState<Array<{ message?: string; name: string; ok: boolean }>>([]);
  const showToast = useToast();
  const publishable = plans.filter((plan) => plan.publishedVersion !== null);

  // Knowing who already has this plan turns a 409 ALREADY_ASSIGNED round trip
  // into something the author can see before choosing.
  useEffect(() => {
    void api<{ assignments: Array<{ planKey: string; userId: string }> }>(`/api/onboarding/assignments?teamId=${encodeURIComponent(teamId)}`)
      .then((result) => setAssigned(result.assignments.map(({ planKey, userId }) => ({ planKey, userId }))))
      .catch(() => setAssigned([]));
  }, [teamId, onAssigned]);

  const selectedPlan = publishable.find((plan) => plan.id === form.planId);
  const takenUserIds = new Set(assigned.filter((entry) => entry.planKey === selectedPlan?.key).map((entry) => entry.userId));
  const sameParty = Boolean(form.mentorUserId) && form.userIds.includes(form.mentorUserId);

  const toggleJoiner = (userId: string, checked: boolean) => setForm((current) => ({
    ...current,
    userIds: checked ? [...current.userIds, userId] : current.userIds.filter((id) => id !== userId),
  }));

  // The assignment API is one learner per call (each gets their own
  // onboarding_assignments row) -- assigning a cohort just fires it once per
  // selected person and reports individually, rather than needing any new
  // bulk endpoint or schema.
  const assign = async () => {
    setBusy(true);
    setLastResults([]);
    try {
      const outcomes = await Promise.all(form.userIds.map(async (userId) => {
        const name = members.find((member) => member.id === userId)?.displayName ?? userId;
        try {
          const result = await api<{ materialisedTaskCount: number }>(`/api/onboarding/plans/${form.planId}/assignments`, {
            method: "POST",
            body: JSON.stringify({
              mentorUserId: form.mentorUserId,
              startedOn: form.startedOn,
              targetOn: form.targetOn || undefined,
              userId,
            }),
          });
          return { message: `${result.materialisedTaskCount} tasks created.`, name, ok: true };
        } catch (reason) {
          return { message: reason instanceof Error ? reason.message : "Could not assign.", name, ok: false };
        }
      }));
      setLastResults(outcomes);
      const succeeded = outcomes.filter((entry) => entry.ok);
      if (succeeded.length) {
        showToast(
          `Assigned ${succeeded.length} of ${outcomes.length} learner${outcomes.length === 1 ? "" : "s"}.`,
          succeeded.length === outcomes.length ? "success" : "error",
        );
        setForm((current) => ({ ...current, mentorUserId: "", targetOn: "", userIds: [] }));
        onAssigned();
      } else {
        showToast("Could not assign any of the selected learners.", "error");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-header fw-semibold">Assign a plan</div>
      <div className="card-body">
        {publishable.length === 0 ? (
          <p className="small text-body-secondary mb-0">
            No plan has a published version yet. Open a plan, finish the draft, and publish it to make it assignable.
          </p>
        ) : (
          <>
            <div className="row g-2 align-items-start">
              <div className="col-md-3"><Field label="Plan">
                <select className="form-select form-select-sm" value={form.planId} onChange={(event) => setForm({ ...form, planId: event.target.value })}>
                  <option value="">Choose a plan…</option>
                  {publishable.map((plan) => <option key={plan.id} value={plan.id}>{plan.title}</option>)}
                </select>
              </Field></div>
              <div className="col-md-3">
                <span className="fw-semibold d-block small mb-1" id="assign-joiners-label">New joiners</span>
                <div
                  className="border rounded p-2 overflow-auto"
                  style={{ maxHeight: "8rem" }}
                  role="group"
                  aria-labelledby="assign-joiners-label"
                >
                  {members.map((member) => {
                    const taken = takenUserIds.has(member.id);
                    return (
                      <div className="form-check" key={member.id}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`assign-joiner-${member.id}`}
                          checked={form.userIds.includes(member.id)}
                          disabled={taken}
                          onChange={(event) => toggleJoiner(member.id, event.target.checked)}
                        />
                        <label className="form-check-label small" htmlFor={`assign-joiner-${member.id}`}>
                          {member.displayName}{taken ? " — already assigned" : ""}
                        </label>
                      </div>
                    );
                  })}
                </div>
                {form.userIds.length > 1 && <p className="small text-body-secondary mb-0 mt-1">{form.userIds.length} people selected.</p>}
              </div>
              <div className="col-md-3"><Field label="Mentor" hint="Reviews and approves the evidence.">
                <select className={`form-select form-select-sm ${sameParty ? "is-invalid" : ""}`} value={form.mentorUserId} onChange={(event) => setForm({ ...form, mentorUserId: event.target.value })}>
                  <option value="">Choose a mentor…</option>
                  {members.filter((member) => !form.userIds.includes(member.id)).map((member) => (
                    <option key={member.id} value={member.id}>{member.displayName}</option>
                  ))}
                </select>
              </Field></div>
              <div className="col-md-3">
                <div className="row g-2">
                  <div className="col"><Field label="Start date"><input type="date" className="form-control form-control-sm" value={form.startedOn} onChange={(event) => setForm({ ...form, startedOn: event.target.value })} /></Field></div>
                  <div className="col"><Field label="Target end" hint="Optional."><input type="date" className="form-control form-control-sm" min={form.startedOn} value={form.targetOn} onChange={(event) => setForm({ ...form, targetOn: event.target.value })} /></Field></div>
                </div>
              </div>
            </div>
            {sameParty && (
              <p className="small text-danger mb-2">
                The mentor must be someone other than the new joiner — nobody can approve their own evidence.
              </p>
            )}
            <button
              className="btn btn-sm btn-primary"
              disabled={busy || !form.planId || !form.userIds.length || !form.mentorUserId || sameParty}
              onClick={() => void assign()}
            >
              {form.userIds.length > 1 ? `Assign to ${form.userIds.length} people` : "Assign plan"}
            </button>
            {lastResults.some((entry) => !entry.ok) && (
              <ul className="small text-danger mb-0 mt-2">
                {lastResults.filter((entry) => !entry.ok).map((entry) => <li key={entry.name}>{entry.name}: {entry.message}</li>)}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function OnboardingAuthoring({ teamId, members, teams, onAssigned }: {
  teamId: string; members: Member[]; teams: Array<{ id: string; name: string }>; onAssigned?: () => void;
}) {
  const [plans, setPlans] = useState<PlanListItem[] | null>(null);
  const [error, setError] = useState("");
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [newPlan, setNewPlan] = useState({ key: "", title: "" });
  const [creating, setCreating] = useState(false);
  const showToast = useToast();

  const load = useCallback(async () => {
    const result = await api<{ plans: PlanListItem[] }>(`/api/onboarding/plans?teamId=${encodeURIComponent(teamId)}`);
    setPlans(result.plans);
  }, [teamId]);

  useEffect(() => { void load().catch((reason) => setError(reason instanceof Error ? reason.message : "Could not load plans.")); }, [load]);

  // Assigning has to refresh the page that owns the assignment list too --
  // otherwise assigning a plan to yourself leaves "My onboarding" claiming you
  // have none until a full reload. Memoised because AssignPanel keys an effect
  // off this identity.
  const handleAssigned = useCallback(async () => {
    await load();
    onAssigned?.();
  }, [load, onAssigned]);

  const sortedPlans = useMemo(
    () => [...(plans ?? [])].sort((a, b) => a.title.localeCompare(b.title)),
    [plans],
  );

  const createPlan = async () => {
    try {
      const result = await api<{ planId: string }>("/api/onboarding/plans", { method: "POST", body: JSON.stringify({ key: newPlan.key, teamId, title: newPlan.title }) });
      setNewPlan({ key: "", title: "" });
      setCreating(false);
      await load();
      setEditingPlanId(result.planId);
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not create plan.", "error");
    }
  };

  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  if (!plans) return <Skeleton label="Loading plans…" variant="list" />;
  if (editingPlanId) return <PlanEditor planId={editingPlanId} onClose={() => { setEditingPlanId(null); void load(); }} />;

  return (
    <div>
      <AssignPanel teamId={teamId} members={members} plans={plans} onAssigned={handleAssigned} />

      <div className="card">
        <div className="card-header fw-semibold">Plans</div>
        <ul className="list-group list-group-flush">
          {sortedPlans.length === 0 && (
            <li className="list-group-item small text-body-secondary">
              No onboarding plans yet. Create one to define the stages, tasks and checkpoints a new joiner works through.
            </li>
          )}
          {sortedPlans.map((plan) => (
            <PlanRow key={plan.id} plan={plan} teams={teams} onOpen={() => setEditingPlanId(plan.id)} onChanged={load} />
          ))}
          <li className="list-group-item bg-body-tertiary">
            {creating ? (
              <>
                <div className="d-flex gap-2 flex-wrap align-items-start">
                  <KeyAndTitle titleLabel="Plan title" keyValue={newPlan.key} title={newPlan.title} onChange={setNewPlan} />
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-primary" disabled={!KEY_PATTERN.test(newPlan.key) || !newPlan.title.trim()} onClick={() => void createPlan()}>Create plan</button>
                  <button className="btn btn-sm btn-link" onClick={() => { setNewPlan({ key: "", title: "" }); setCreating(false); }}>Cancel</button>
                </div>
              </>
            ) : (
              <button className="btn btn-sm btn-outline-primary" onClick={() => setCreating(true)}>+ Create a plan</button>
            )}
          </li>
        </ul>
      </div>
    </div>
  );
}
